import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { PlusIcon, ExclamationTriangleIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import { useGetCurrentUserQuery } from '../../../redux/apis'
import useUrlChangeHandler from '../../../hooks/useUrlChangeHandler'
import CardsContainer from '../../../components/cards/CardsContainer'
import ListingManagement from '../../../components/listings/ListingManagement'
import ListingFilter from '../../../components/cards/filter/ListingFilter'
import ListingCard from '../../../components/cards/card/ListingCard'
import Button from '../../../components/ui/Button'
import { setLayoutChanging, clearAll, handleUrlChange } from '../../../redux/slices/filterSlice'

const userLang = navigator.language || navigator.userLanguage

const Listings = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const { data: user } = useGetCurrentUserQuery()
  const [showVerifyMessage, setShowVerifyMessage] = useState(false)
  
  // Check if user has a verified Stripe account
  const isStripeVerified = user?.stripeAccountDetail?.chargesEnabled === true
  
  // Initialize URL handler
  useUrlChangeHandler()
  
  useEffect(() => {
    // Check if redirected from stripe verified route with message
    if (location.state?.message) {
      setShowVerifyMessage(true)
    }
  }, [location])

  // Reset layoutChanging flag and force data refresh when component mounts
  useEffect(() => {
    // Reset the flag when the component mounts to ensure clean state
    dispatch(setLayoutChanging(false))
    
    // Force a refresh by clearing all and requesting new data
    dispatch(clearAll())
    dispatch(handleUrlChange())
    
    return () => {
      // Make sure flag is reset when leaving the page
      dispatch(setLayoutChanging(false))
    }
  }, [dispatch])

  // Add effect to refresh data when location changes (like coming back from create/edit)
  useEffect(() => {
    if (location.state?.refresh) {
      // Reset the refresh flag
      history.replace({
        ...location,
        state: {}
      })
      
      // Force a refresh
      dispatch(clearAll())
      dispatch(handleUrlChange())
    }
  }, [location, history, dispatch])

  // Get all required state from redux
  const { 
    products, 
    loading,
    dataLoading, 
    error,
    hasMore,
    totalResults,
    searchTerm,
    isInitialized, 
    lastSearch 
  } = useSelector(state => state.filter)
  
  const handleCreateListing = () => {
    if (isStripeVerified) {
      history.push('/listing/upload')
    } else {
      setShowVerifyMessage(true)
    }
  }
  
  const goToWallet = () => {
    history.push('/dashboard/wallet')
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {userLang === 'zh-CN' ? '商品列表' : 'My Listings'}
        </h2>
        <Button
          onClick={handleCreateListing}
          variant="primary"
          icon={<PlusIcon className="w-5 h-5" />}
        >
          <span className="hidden sm:inline">Upload Listing</span>
          <span className="sm:hidden">Upload</span>
        </Button>
      </div>
      
      {/* Stripe Verification Alert */}
      {showVerifyMessage && !isStripeVerified && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                {userLang === 'zh-CN' 
                  ? '您需要完成Stripe验证才能上传产品。' 
                  : 'You need to complete Stripe verification before uploading products.'}
              </p>
              <div className="mt-4">
                <Button
                  onClick={goToWallet}
                  variant="secondary"
                  size="sm"
                  icon={<ArrowRightIcon className="w-4 h-4" />}
                >
                  {userLang === 'zh-CN' ? '前往验证' : 'Go to verification'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Management Section */}
      <div className="bg-white shadow-sm rounded-lg mb-6">
        <ListingManagement />
      </div>

      {/* Filter Section */}
      <div className="bg-white shadow-sm rounded-lg mb-6">
        <ListingFilter />
      </div>

      {/* Cards Section */}
      <div className="bg-white shadow-sm rounded-lg">
        <CardsContainer
          loading={loading}
          dataLoading={dataLoading}
          error={error}
          hasMore={hasMore}
          totalResults={totalResults}
          searchTerm={searchTerm}
          isInitialized={isInitialized}
          lastSearch={lastSearch}
        >
          {products.map(product => (
            <ListingCard 
              key={product.id} 
              product={product}
            />
          ))}
        </CardsContainer>
      </div>
    </div>
  )
}

export default Listings
